/**
 * CartWarningModal.jsx
 * Created by Michael Bray on 2/14/18.
 */

import React from 'react';
import moment from 'moment';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import * as Icons from '../../sharedComponents/icons/Icons';
import SessionStorage from '../../../helpers/storage/SessionStorage';
import Heading from '../../sharedComponents/Heading';
import Button from '../../sharedComponents/Button';
import announce from '../../../helpers/announce';
import { pluralize } from '../../../helpers/stringHelper';

const propTypes = {
    extendTimer: PropTypes.func.isRequired,
    fetchCart: PropTypes.func.isRequired,
    expiration: PropTypes.string,
    sessionWarning: PropTypes.bool
};

const defaultProps = {
    expiration: '',
    sessionWarning: false
};

export default class CartWarningModal extends React.Component {
    constructor(props) {
        super(props);

        const ignored = SessionStorage.getItem('r1s_cart_ignored') === "true";

        this.state = {
            timer: null,
            timeRemaining: 0,
            modalOpen: false,
            expired: false,
            ignored
        };

        this.tick = this.tick.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.extendTimer = this.extendTimer.bind(this);
        this.renderContent = this.renderContent.bind(this);
    }

    componentDidMount() {
        const timer = setInterval(() => this.tick(), 1000);
        this.setState({ timer });
    }

    componentWillUnmount() {
        clearInterval(this.state.timer);
    }

    tick() {
        this.setState({
            timeRemaining: -1 * moment().diff(this.props.expiration)
        });
        if (this.state.timeRemaining <= 0) {
            clearInterval(this.state.timer);
            this.setState({
                modalOpen: true,
                expired: true
            });
            SessionStorage.removeItem('r1s_cart_ignored');
            return;
        }
        if (this.state.timeRemaining <= 300000 && !this.state.ignored && !this.props.sessionWarning) {
            this.setState({ modalOpen: true });
        }
    }

    formatTime(num) {
        if (num <= 0) {
            return '00';
        }
        return num < 10 ? `0${num}` : num.toString();
    }

    closeModal(fetchCart) {
        this.setState({
            modalOpen: false,
            ignored: true
        });
        SessionStorage.setItem('r1s_cart_ignored', "true");
        if (fetchCart) {
            this.props.fetchCart();
        }
    }

    extendTimer() {
        announce("You have extended your cart timer by five minutes.")
        this.props.extendTimer();
        this.closeModal();
    }

    renderTime() {
        const mm = this.formatTime(moment.duration(this.state.timeRemaining).get('minutes'));
        const ss = this.formatTime(moment.duration(this.state.timeRemaining).get('seconds'));

        const minuteText = (
            <span className="rec-sr-only">
                {pluralize(mm, 'minute', 'minutes')}
            </span>
        );

        const secondText = (
            <span className="rec-sr-only">
                {pluralize(ss, 'second', 'seconds')}
            </span>
        );

        return (
            <React.Fragment>
                {mm}{minuteText}
                <span aria-hidden="true">:</span>
                {ss}{secondText}
            </React.Fragment>
        );
    }

    renderContent() {
        if (this.state.expired) {
            return (
                <div>
                    <div className="rec-modal-content-wrap">
                        <div>
                            <Heading
                                headingLevel={1}
                                appearance="display"
                                className="rec-modal-title"
                                id="cartWarningModalHeading">
                                Your cart has expired.
                            </Heading>
                            <p>
                                The time ran out on your cart, and items have been
                                removed. The good news is that we&#39;re still
                                here, so let&#39;s retrace your steps or take a
                                look for new adventures.
                            </p>
                        </div>
                    </div>
                    <div className="rec-modal-actions-wrap">
                        <Button
                            title="Plan Your Trip"
                            aria-label="Plan Your Trip"
                            onClick={() => window.location.assign('/')}>
                            Plan Your Trip
                        </Button>
                    </div>
                </div>
            );
        }
        return (
            <div>
                <div className="rec-modal-content-wrap">
                    <Button
                        appearance="subtle"
                        iconBeforeElement={<Icons.IconClose />}
                        screenReaderTextBefore="Close Cart Warning Dialog"
                        onClick={this.closeModal}
                        className="rec-close-modal" />
                    <Heading
                        headingLevel={1}
                        appearance="display"
                        className="rec-modal-title"
                        id="cartWarningModalHeading">
                        Your cart is about to expire.
                    </Heading>
                    <p>
                        This is a friendly reminder that your cart will time out
                        in {this.renderTime()}. Once time is up, all items will
                        be removed from your cart.
                    </p>
                    <p>
                        We can provide more time to finish this reservation,
                        though! Would five more minutes be helpful?
                    </p>
                </div>
                <div className="rec-modal-actions-wrap">
                    <Button
                        title="Add Five Minutes"
                        aria-label="Add Five Minutes"
                        onClick={this.extendTimer}>
                        Add Five Minutes
                    </Button>
                    <Button
                        appearance="tertiary"
                        title="Ignore"
                        aria-label="Ignore"
                        onClick={this.closeModal}>
                        Ignore
                    </Button>
                </div>
            </div>
        );
    }

    render() {
        return (
            <Modal
                role="complementary"
                isOpen={this.state.modalOpen}
                className={{
                    base: 'rec-modal-dialog-base',
                    afterOpen: 'rec-modal-dialog-after-open-md',
                    beforeClose: 'rec-modal-dialog-before-close'
                }}
                overlayClassName={{
                    base: 'rec-overlay-base',
                    afterOpen: 'rec-overlay-after-open',
                    beforeClose: 'rec-overlay-before-close'
                }}
                aria={{
                    labelledby: "cartWarningModalHeading"
                }}
                ariaHideApp={false}>
                {this.renderContent()}
            </Modal>
        );
    }
}

CartWarningModal.propTypes = propTypes;
CartWarningModal.defaultProps = defaultProps;
